import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { luuTep } from '../../../lib/luu-tep';
import { DANH_BA_TOI_DA, nhiemVuRepository } from '../../../lib/repositories/van-ban-nhiem-vu.repository';
import { useToast } from '../../../lib/toast';
import { nguoiTrungTen } from '../nghiep-vu/nhiem-vu';
import type {
  CapNhatVao,
  GiaoNhiemVuVao,
  HoatDong,
  LocCay,
  LocKhoangNgay,
  NguoiDanhBa,
  NguoiThamGiaVao,
  NutNhiemVu,
  TrangThaiNhiemVu,
  TruongNhiemVu,
  ViecConVao,
} from '../types-nhiem-vu';

/**
 * Hook của màn Quản lý nhiệm vụ và Thông báo, chuyển từ `useNhiemVu.ts` của foxainative. Ghi xong
 * vô hiệu cả nhánh `nhiem-vu` (một thao tác có thể đổi trạng thái việc cha, thống kê và thông báo).
 */
const GOC = ['van-ban', 'nhiem-vu'] as const;
const KHOA = {
  thongKe: (f: LocKhoangNgay) => [...GOC, 'thong-ke', f] as const,
  cay: (f: LocCay) => [...GOC, 'cay', f] as const,
  mot: (id: string | null) => [...GOC, 'mot', id] as const,
  dongThoiGian: (id: string | null) => [...GOC, 'dong-thoi-gian', id] as const,
  thongBao: [...GOC, 'thong-bao'] as const,
  danhBa: ['van-ban', 'danh-ba'] as const,
};
const THONG_BAO_MOI_MS = 60_000;
const SO_KHOP_TEN = 10;

export function useThongKeNhiemVu(f: LocKhoangNgay) {
  return useQuery({ queryKey: KHOA.thongKe(f), queryFn: () => nhiemVuRepository.thongKe(f), staleTime: 30_000 });
}

export function useCayNhiemVu(f: LocCay) {
  return useQuery({
    queryKey: KHOA.cay(f),
    queryFn: () => nhiemVuRepository.cay(f),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

/** Việc mở từ thông báo có thể không nằm trong trang hoặc bộ lọc hiện tại — tải riêng nó. */
export function useNutNhiemVu(id: string | null, bat: boolean) {
  return useQuery({
    queryKey: KHOA.mot(id),
    queryFn: () => nhiemVuRepository.mot(id ?? ''),
    enabled: bat && id !== null,
    staleTime: 15_000,
  });
}

export function useDonViDanhBa() {
  return useQuery({
    queryKey: [...KHOA.danhBa, 'don-vi'],
    queryFn: nhiemVuRepository.donViDanhBa,
    staleTime: 600_000,
    select: (r) => r.items,
  });
}

export function useNguoiDanhBa(tim: string, bat: boolean) {
  return useQuery({
    queryKey: [...KHOA.danhBa, 'nguoi', tim],
    queryFn: () => nhiemVuRepository.nguoiDanhBa(tim || undefined, 20),
    staleTime: 300_000,
    enabled: bat,
    select: (r) => r.items,
  });
}

/** Khớp tên cá nhân AI chọn với tài khoản danh bạ: chỉ nhận khi trùng tên đầy đủ và duy nhất. */
export function useNguoiTrungTen(dsTen: string[]) {
  const ten = [...new Set(dsTen.filter(Boolean))];
  const kq = useQueries({
    queries: ten.map((t) => ({
      queryKey: [...KHOA.danhBa, 'trung-ten', t],
      queryFn: () => nhiemVuRepository.nguoiDanhBa(t, Math.min(SO_KHOP_TEN, DANH_BA_TOI_DA)),
      staleTime: 300_000,
    })),
  });
  const theoTen = new Map<string, NguoiDanhBa | null>();
  ten.forEach((t, i) => {
    const du = kq[i]?.data;
    if (du) theoTen.set(t, nguoiTrungTen(t, du.items));
  });
  return { theoTen, dangKhop: kq.some((r) => r.isLoading) };
}

/** Ghi: thành công → vô hiệu cache + toast đã hoàn tất; lỗi → toast câu lỗi. */
function useGhi<V, R>(ham: (v: V) => Promise<R>, thongBao: (v: V) => { khoa: KhoaDich; so?: number } | null) {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useT();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: ham,
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: GOC });
      const tb = thongBao(v);
      if (tb) toast.success(t(tb.khoa, tb.so === undefined ? undefined : { so: tb.so }));
    },
    onError: (err) => {
      // Bước sửa trường có thể đã lưu trước khi bước sau lỗi — tải lại để màn hình khớp máy chủ.
      void qc.invalidateQueries({ queryKey: GOC });
      toast.error(dichLoi(err));
    },
  });
}

export function useGiaoNhiemVu() {
  return useGhi((vao: GiaoNhiemVuVao) => nhiemVuRepository.tao(vao), () => ({ khoa: 'vanBanNhiemVu.toast.daGiao' }));
}

export function useSuaNhiemVu() {
  return useGhi(
    async (v: { id: string; truong: TruongNhiemVu; nguoi: NguoiThamGiaVao[] }) => {
      await nhiemVuRepository.sua(v.id, v.truong);
      return nhiemVuRepository.nguoiThamGia(v.id, v.nguoi);
    },
    () => ({ khoa: 'vanBanNhiemVu.toast.daLuu' }),
  );
}

export function useThemNguoiThamGia() {
  return useGhi(
    (v: { nv: NutNhiemVu; nguoi: NguoiThamGiaVao }) =>
      nhiemVuRepository.nguoiThamGia(v.nv.id, [
        ...v.nv.participants.map(({ id: _id, ...p }): NguoiThamGiaVao => p),
        v.nguoi,
      ]),
    () => ({ khoa: 'vanBanNhiemVu.toast.daThemNguoi' }),
  );
}

export function useDoiTrangThai() {
  return useGhi(
    (v: { id: string; status: TrangThaiNhiemVu }) => nhiemVuRepository.trangThai(v.id, v.status),
    () => ({ khoa: 'vanBanNhiemVu.toast.daDoiTrangThai' }),
  );
}

export function useXoaNhiemVu() {
  return useGhi((id: string) => nhiemVuRepository.xoa(id), () => ({ khoa: 'vanBanNhiemVu.toast.daXoa' }));
}

export function useTaoViecCon() {
  return useGhi(
    (v: { id: string; con: ViecConVao[] }) => nhiemVuRepository.taoViecCon(v.id, v.con),
    (v) => ({ khoa: 'vanBanNhiemVu.toast.daTaoCon', so: v.con.length }),
  );
}

export function useThemCapNhat() {
  return useGhi(
    (v: { id: string; vao: CapNhatVao }) => nhiemVuRepository.themCapNhat(v.id, v.vao),
    (v) => ({ khoa: v.vao.kind === 'PROGRESS' ? 'vanBanNhiemVu.toast.daLuuTienDo' : 'vanBanNhiemVu.toast.daGuiYKien' }),
  );
}

/** Gợi ý chưa lưu gì nên không vô hiệu cache; lỗi hiện ngay trong hộp, không toast. */
export function useGoiYPhanRa() {
  return useMutation({ mutationFn: (id: string) => nhiemVuRepository.goiYPhanRa(id) });
}

export function useTaiTepNhiemVu() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: async (nv: NutNhiemVu) => luuTep(await nhiemVuRepository.tep(nv.id), nv.attachment_filename ?? nv.id),
    onError: (err) => toast.error(dichLoi(err)),
  });
}

export function useTaiTepCapNhat() {
  const toast = useToast();
  const dichLoi = useLoi();
  return useMutation({
    mutationFn: async (v: { taskId: string; hd: HoatDong }) =>
      luuTep(await nhiemVuRepository.tepCapNhat(v.taskId, v.hd.id), v.hd.attachment_filename ?? v.hd.id),
    onError: (err) => toast.error(dichLoi(err)),
  });
}

export function useDongThoiGian(id: string | null) {
  return useQuery({
    queryKey: KHOA.dongThoiGian(id),
    queryFn: () => nhiemVuRepository.dongThoiGian(id ?? ''),
    enabled: id !== null,
    staleTime: 15_000,
  });
}

export function useThongBaoNhiemVu(limit: number) {
  return useQuery({
    queryKey: [...KHOA.thongBao, limit],
    queryFn: () => nhiemVuRepository.thongBao(limit),
    staleTime: 30_000,
    refetchInterval: THONG_BAO_MOI_MS,
  });
}

export function useDanhDauDaDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => nhiemVuRepository.daXem(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KHOA.thongBao }),
  });
}
